import Button from "../buttons/Button";
import CheckBoxWithLabel from "../checkbox/CheckBoxWithLabel";
import DropdownButtonForSearch from "../Dropdowns/DropdownButtonForSearch";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import Image from "../images/Image";
import MainModel from "./MainModel";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

interface PinVerificationModelProps {
  handleClose: () => void;
  pin: string[];
  setPin: (pin: string[]) => void;
  handleDropdownChange: (key?: string, value? : string | boolean | any[]) => void;
  selectedDropDownValue: string;
  handleClickVerify: () => void;
  verifyLoading: boolean;
  errorMessage: string;
  setErrorMessage: (errorMessage: string) => void;
  isDontAskAgainChecked?: boolean;
  dropdownError? : string;
}

const imageVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const headingVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

export default function PinVerificationModel({
  handleClose,
  pin,
  setPin,
  dropdownError,
  handleDropdownChange,
  handleClickVerify,
  errorMessage, setErrorMessage,
  verifyLoading,
  isDontAskAgainChecked,
  selectedDropDownValue,
}: PinVerificationModelProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [dropDownOpen, setDropDownOpen] = useState(false);

  useEffect(() => {
    const handleGlobalKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !verifyLoading) {
        handleClickVerify();
      }
    };
    
    // Add event listener
    document.addEventListener('keydown', handleGlobalKeyPress);
    
    // Clean up
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyPress);
    };
  }, [handleClickVerify]);
  
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, pin.length);
    const updatedPin = [...pin];

    pasteData.split("").forEach((digit, i) => {
      if (i < pin.length) {
        updatedPin[i] = digit;
      }
    });

    setPin(updatedPin);

    const nextIndex =
      pasteData.length >= pin.length ? pin.length - 1 : pasteData.length;
    inputRefs.current[nextIndex]?.focus();
  };

  const handleChange = (value: string, index: number) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue.length > 1) return;
    setErrorMessage("");

    const updatedPin = [...pin];
    updatedPin[index] = numericValue;
    setPin(updatedPin);

    if (numericValue && index < pin.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setTimeout(() => setCaretToEnd(index - 1), 0);
    }

    if (e.key === "ArrowRight" && index < pin.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (e.key === "Backspace") {
      if (!pin[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setTimeout(() => setCaretToEnd(index - 1), 0);
      }
    }
  };

  const setCaretToEnd = (index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      const length = input.value.length;
      input.setSelectionRange(length, length);
    }
  };
  const handleCheckedDontAskAgain = () => {
    handleDropdownChange('isDontAskAgainChecked', !isDontAskAgainChecked)
    handleDropdownChange("pin_interval", "");
  };

  return (
    <MainModel
      isClose={true}
      loginFlow={false}
      modelWidth={`w-full max-w-[480px] ${dropDownOpen ? "h-[730px]" : ""}`}
      handleClose={handleClose}
      disableOutsideClick={true} // Add this prop to prevent closing on outside click
    >
      <motion.div
        variants={imageVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.4 }}
        className="flex items-center justify-center"
      >
        <Image src={"otp_icon"} className="" />
      </motion.div>
      <motion.h1
        variants={headingVariants}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5, ease: "easeInOut", delay: 0.2 }}
        className="text-primary_text text-heading2 text-center pt-6"
      >
        Enter PIN
      </motion.h1>
      <div className="flex flex-col items-center mt-5">
        <div className="flex gap-4">
          {pin.map((digit: string, index: number) => (
            <input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              id={`digit-${index}`}
              maxLength={1}
              autoComplete="off"
              value={digit ? '●' : ''}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              placeholder="*"
              className={`w-[54px] h-14 text-center text-lg border rounded-md ${
                errorMessage
                  ? "border-negative"
                  : "border-gray-300 focus:border-blue-500"
              } focus:outline-none placeholder-gray-400 transition-colors`}
            />
          ))}
        </div>

      </div>
      {errorMessage && <div className="ml-[86px] mt-2"><ErrorMessage errorMessage={errorMessage} /></div>}
      <div className={`flex justify-center`}>
        <Button
          buttonName={"Verify"}
          className={`mt-6 w-[320px] `}
          loading={verifyLoading}
          buttonWidth="w-[262px]"
          buttonType={true}
          testId={"pin_verify"}
          buttonHight="h-[50px]"
          buttonOnclick={handleClickVerify}
        />
      </div>
      <div className="mt-6 flex justify-center">
        <CheckBoxWithLabel
          label={"Don't ask again?"}
          handelCheckBox={() => handleCheckedDontAskAgain()}
          checked={isDontAskAgainChecked}
          id={`credit_status`}
          textClassName={"text-secondary_text text-SubTitle cursor-pointer"}
          fontWeight="font-[500]"
        />{" "}
      </div>
      {isDontAskAgainChecked && (
        <>
          <div className="justify-center flex mt-4">
            <p className="text-primary_text text-subTitle">
              When would you like to ask the PIN Again ?{" "}
              <span className="text-negative">*</span>
            </p>
          </div>
          <div className="flex justify-center mt-4">
            <DropdownButtonForSearch
              labelName=""
              id="pin_interval"
              noSearch={true}
              errorMessage={dropdownError}
              onChange={handleDropdownChange}
              required={false}
              tabIndex={1}
              options={["1 day", "1 week", "1 month"]}
              selectedValue={selectedDropDownValue}
              dropdownTopClass={'top-[65px]'}
              dropDownOpen={dropDownOpen}
              setDropDownOpen={setDropDownOpen}
              toolTipClassName={'-top-[0.5rem]'}
            />
          </div>
        </>
      )}
    </MainModel>
  );
}